import { AlertTriangle } from 'lucide-react'
import { useSubscriptionStatus, formatSubscriptionExpiry } from '@/hooks/useSubscriptionStatus'

/**
 * Tells a learner their subscription has lapsed, before they walk into a
 * refusal.
 *
 * The backend holds back booking, joining and submitting once a subscription
 * expires while leaving everything readable. Without this the first sign of
 * that is a failed action, so the banner names the date and says plainly what
 * still works.
 *
 * Renders nothing for active subscriptions and for learners who have none —
 * the common case, since most workspaces never sold a package.
 */
export const SubscriptionExpiredBanner = () => {
  const { isExpired, expiresAt } = useSubscriptionStatus()

  if (!isExpired) return null

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-amber-900">
          Your subscription ended{expiresAt ? ` on ${formatSubscriptionExpiry(expiresAt)}` : ''}
        </p>
        <p className="mt-0.5 text-amber-800">
          You can still open your certificates, past courses and calendar. Booking
          sessions, joining them and submitting assessments are paused until it is
          renewed — contact your administrator to continue.
        </p>
      </div>
    </div>
  )
}
